import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Alert,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useTheme } from '../../contexts/ThemeContext';
import { tokens } from '../../theme/tokens';
import type { SelectChangeEvent } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import UnitPageShell from '../../components/shared/UnitPageShell';
import { useAuth } from '../../context/AuthContext';

type MethodKey = 'ventilator' | 'anemometer' | 'orifice';
type DuctShape = 'round' | 'rectangular';
type FieldName =
  | 'volumeFlow'
  | 'effectivePressure'
  | 'kFactor'
  | 'airSpeed'
  | 'ductDiameter'
  | 'ductWidth'
  | 'ductHeight'
  | 'diffPressure'
  | 'alpha'
  | 'area'
  | 'airDensity';

type FormValues = Record<FieldName, string>;

const defaultValues: FormValues = {
  volumeFlow: '',
  effectivePressure: '',
  kFactor: '',
  airSpeed: '',
  ductDiameter: '',
  ductWidth: '',
  ductHeight: '',
  diffPressure: '',
  alpha: '',
  area: '',
  airDensity: '1.204'
};

const FIELD_LABELS: Record<FieldName, string> = {
  volumeFlow: 'Volumenstrom Q (m³/h)',
  effectivePressure: 'Wirkdruck Δp (Pa)',
  kFactor: 'K-Faktor',
  airSpeed: 'Luftgeschwindigkeit v (m/s)',
  ductDiameter: 'Durchmesser d (mm)',
  ductWidth: 'Breite a (mm)',
  ductHeight: 'Höhe b (mm)',
  diffPressure: 'Differenzdruck Δp (Pa)',
  alpha: 'Durchflusskoeffizient α',
  area: 'Öffnungsfläche A₀ (m²)',
  airDensity: 'Luftdichte ρ (kg/m³)'
};

const BASE_HELPERS: Record<FieldName, string> = {
  volumeFlow: 'Lass genau ein Feld frei – dieses wird berechnet.',
  effectivePressure: 'Wirkdruck in Pascal. Negative Werte erlauben eventuell Unterdruck.',
  kFactor: 'Gerätespezifischer K-Faktor.',
  airSpeed: 'Gemittelte Geschwindigkeit an der Messstelle.',
  ductDiameter: 'Innen-Ø des Kanals in Millimetern.',
  ductWidth: 'Innenbreite des Kanals in Millimetern.',
  ductHeight: 'Innenhöhe des Kanals in Millimetern.',
  diffPressure: 'Differenzdruck über Messblende oder Venturi (Pa).',
  alpha: 'Durchflusskoeffizient α des Messgeräts.',
  area: 'Wirksame Öffnungsfläche A₀ in Quadratmetern.',
  airDensity: 'Standard-Luftdichte ρ (typisch 1.204 kg/m³).'
};

const METHOD_OPTIONS: { key: MethodKey; label: string }[] = [
  { key: 'ventilator', label: 'Wirkdruckmessung (Ventilator)' },
  { key: 'anemometer', label: 'Anemometer / Kanalmessung' },
  { key: 'orifice', label: 'Messblende / Venturi' }
];

const METHOD_DESCRIPTIONS: Record<MethodKey, string> = {
  ventilator:
    'Wirkdruckmessung am Ventilator: Q = K · √Δp  Q: Volumenstrom (m³/h) K: K-Faktor des Ventilators Δp: Wirkdruck am Ventilator (Pa)',
  anemometer:
    'Anemometermessung im Kanal: Q = v · A · 3600  Q: Volumenstrom (m³/h) v: Luftgeschwindigkeit (m/s) A: Kanalquerschnittsfläche (m²)',
  orifice:
    'Messblende/Venturidüse: Q = α · A₀ · √(2 · Δp / ρ) · 3600  Q: Volumenstrom (m³/h) α: Durchflusskoeffizient A₀: Querschnitt Blende/Düse (m²) Δp: Wirkdruck (Pa) ρ: Luftdichte (kg/m³)  (Expansionszahl ε wird hier vereinfachend mit 1 angenommen.)'
};



// Removed hardcoded cardSx and redColor - using tokens throughout

const normalizeNumber = (value: string): string => value.replace(/,/g, '.');

const digitsForField = (field: FieldName): number => {
  switch (field) {
    case 'alpha':
    case 'airDensity':
    case 'kFactor':
      return 3;
    case 'area':
      return 4;
    default:
      return 2;
  }
};

const formatNumber = (value: number, digits: number): string =>
  new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: Number.isInteger(value) ? 0 : Math.min(digits, 4),
    maximumFractionDigits: digits
  }).format(value);

const formatForField = (field: FieldName, value: number): string =>
  formatNumber(value, digitsForField(field));

const getVisibleFields = (method: MethodKey, shape: DuctShape): FieldName[] => {
  switch (method) {
    case 'ventilator':
      return ['volumeFlow', 'effectivePressure', 'kFactor'];
    case 'anemometer':
      return shape === 'round'
        ? ['volumeFlow', 'airSpeed', 'ductDiameter']
        : ['volumeFlow', 'airSpeed', 'ductWidth', 'ductHeight'];
    case 'orifice':
      return ['volumeFlow', 'diffPressure', 'alpha', 'area', 'airDensity'];
    default:
      return [];
  }
};

const Page: React.FC = () => {
  const { darkMode } = useTheme();
  const { canViewVolumenstrom } = useAuth();
  const [method, setMethod] = useState<MethodKey>('ventilator');
  const [ductShape, setDuctShape] = useState<DuctShape>('round');
  const [values, setValues] = useState<FormValues>(defaultValues);
  const [targetField, setTargetField] = useState<FieldName | null>('volumeFlow');
  const [result, setResult] = useState<string>('Ergebnis wird hier angezeigt');
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [infoOpen, setInfoOpen] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'info' | 'warning' | 'error' } | null>(null);

  const visibleFields = useMemo(() => getVisibleFields(method, ductShape), [method, ductShape]);
  const isFieldVisible = useCallback((field: FieldName) => visibleFields.includes(field), [visibleFields]);

  const labelForField = useCallback(
    (field: FieldName) => (targetField === field ? `${FIELD_LABELS[field]} (Ziel)` : FIELD_LABELS[field]),
    [targetField]
  );

  const helperForField = useCallback(
    (field: FieldName) => {
      if (errors[field]) {
        return errors[field] as string;
      }
      if (targetField === field) {
        return 'Dieses Feld wird aus den übrigen Werten berechnet.';
      }
      return BASE_HELPERS[field];
    },
    [errors, targetField]
  );

  const handleMethodChange = (event: SelectChangeEvent<MethodKey>) => {
    const nextMethod = event.target.value as MethodKey;
    setMethod(nextMethod);
    setValues({ ...defaultValues });
    setDuctShape('round');
    setTargetField('volumeFlow');
    setResult('Ergebnis wird hier angezeigt');
    setErrors({});
  };

  const handleValueChange = (field: FieldName) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setValues(prev => ({ ...prev, [field]: value }));
    setResult('Ergebnis wird hier angezeigt');
    setErrors(prev => {
      if (!prev[field]) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });
    if (targetField === field) {
      setTargetField(null);
    }
  };

  const handleFocus = (field: FieldName) => () => {
    if (targetField === field) {
      setTargetField(null);
    }
    setResult('Ergebnis wird hier angezeigt');
  };

  const handleDuctShapeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextShape = event.target.value as DuctShape;
    setDuctShape(nextShape);
    setValues(prev => ({
      ...prev,
      ductDiameter: nextShape === 'round' ? prev.ductDiameter : '',
      ductWidth: nextShape === 'rectangular' ? prev.ductWidth : '',
      ductHeight: nextShape === 'rectangular' ? prev.ductHeight : ''
    }));
    if (
      (nextShape === 'round' && (targetField === 'ductWidth' || targetField === 'ductHeight')) ||
      (nextShape === 'rectangular' && targetField === 'ductDiameter')
    ) {
      setTargetField('volumeFlow');
    }
    setErrors(prev => {
      const { ductDiameter, ductWidth, ductHeight, ...rest } = prev;
      return rest;
    });
    setResult('Ergebnis wird hier angezeigt');
  };

  const clearAllFields = useCallback(() => {
    setValues({ ...defaultValues });
    setTargetField('volumeFlow');
    setErrors({});
    setResult('Ergebnis wird hier angezeigt');
  }, []);

  const handleCopyResult = useCallback(() => {
    if (result && result !== 'Ergebnis wird hier angezeigt') {
      const textContent = result.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
      navigator.clipboard.writeText(textContent);
      setSnackbar({ message: 'Ergebnis in Zwischenablage kopiert', severity: 'success' });
    }
  }, [result]);

  const renderField = (field: FieldName) => (
    <TextField
      key={field}
      fullWidth
      label={labelForField(field)}
      value={values[field]}
      onChange={handleValueChange(field)}
      onFocus={handleFocus(field)}
      error={Boolean(errors[field])}
      helperText={helperForField(field)}
      inputMode="decimal"
      sx={targetField === field ? { 
        '& .MuiInputBase-root': { 
          backgroundColor: darkMode ? 'rgba(100, 150, 255, 0.2)' : 'rgba(213, 226, 255, 0.4)' 
        } 
      } : undefined}
    />
  );

  // Kernlogik: ermittelt das Ziel-Feld und führt die zur Methode passende Berechnung aus.
  const handleCalculate = () => {
    const candidateFields = visibleFields.filter(field => field !== 'airDensity');
    const blanks = candidateFields.filter(field => !values[field]?.trim());

    let nextTarget: FieldName;
    if (targetField && targetField !== 'airDensity' && candidateFields.includes(targetField) && !values[targetField]?.trim()) {
      nextTarget = targetField;
    } else if (blanks.length === 1) {
      nextTarget = blanks[0];
    } else if (blanks.length === 0) {
      nextTarget = 'volumeFlow';
    } else {
      const multiErrors = blanks.reduce<Partial<Record<FieldName, string>>>((acc, field) => {
        acc[field] = 'Genau ein Feld frei lassen – dieses wird berechnet.';
        return acc;
      }, {});
      setErrors(prev => ({ ...prev, ...multiErrors }));
      setResult('Bitte genau ein Feld leer lassen.');
      return;
    }

    if (!candidateFields.includes(nextTarget)) {
      nextTarget = 'volumeFlow';
    }

    setTargetField(nextTarget);

    const newErrors: Partial<Record<FieldName, string>> = {};
    const recordError = (field: FieldName, message: string) => {
      newErrors[field] = message;
    };

    // Liest ein Eingabefeld als Zahl und validiert Sonderfälle (0, negative Werte, Pflichtfelder).
    const readValue = (
      field: FieldName,
      options: { allowZero?: boolean; allowNegative?: boolean; optional?: boolean } = {}
    ): number | null => {
      if (field === nextTarget) return null;
      if (!isFieldVisible(field) && field !== 'airDensity') return null;
      const raw = values[field]?.trim();
      if (!raw) {
        if (!options.optional) recordError(field, 'Pflichtfeld');
        return null;
      }
      const numeric = Number(normalizeNumber(raw));
      if (!Number.isFinite(numeric)) {
        recordError(field, 'Ungültige Zahl');
        return null;
      }
      if (!options.allowNegative && numeric < 0) {
        recordError(field, 'Darf nicht negativ sein');
        return null;
      }
      if (!options.allowZero && numeric === 0) {
        recordError(field, 'Darf nicht 0 sein');
        return null;
      }
      return numeric;
    };

    let calculatedValue: number | null = null;
    let resultText = '';

    if (method === 'ventilator') {
      const effPressure = readValue('effectivePressure', { allowZero: true, allowNegative: true });
      const kFactor = readValue('kFactor', { allowZero: false });
      const volFlow = readValue('volumeFlow', { allowZero: true });

      switch (nextTarget) {
        case 'volumeFlow':
          if (effPressure != null && kFactor != null) {
            if (kFactor === 0) {
              recordError('kFactor', 'Darf nicht 0 sein');
            } else {
              calculatedValue = kFactor * Math.sqrt(Math.abs(effPressure));
              resultText = `Volumenstrom: ${formatNumber(calculatedValue, 2)} m³/h`;
            }
          } else {
            resultText = 'Wirkdruck und K-Faktor benötigt.';
          }
          break;
        case 'effectivePressure':
          if (volFlow != null && kFactor != null) {
            if (kFactor === 0) {
              recordError('kFactor', 'Darf nicht 0 sein');
            } else {
              calculatedValue = Math.pow(volFlow / kFactor, 2);
              resultText = `Wirkdruck: ${formatNumber(calculatedValue, 2)} Pa`;
            }
          } else {
            resultText = 'Volumenstrom und K-Faktor benötigt.';
          }
          break;
        case 'kFactor':
          if (volFlow != null && effPressure != null && Math.abs(effPressure) > 0) {
            calculatedValue = volFlow / Math.sqrt(Math.abs(effPressure));
            resultText = `K-Faktor: ${formatNumber(calculatedValue, digitsForField('kFactor'))}`;
          } else {
            resultText = 'Volumenstrom und Wirkdruck benötigt.';
            if (effPressure != null && Math.abs(effPressure) === 0) {
              recordError('effectivePressure', 'Darf nicht 0 sein');
            }
          }
          break;
        default:
          resultText = 'Ungültiges Ziel für diese Methode.';
      }
    } else if (method === 'anemometer') {
      const speed = readValue('airSpeed');
      const volFlow = readValue('volumeFlow', { allowZero: true });
      const diameter = ductShape === 'round' ? readValue('ductDiameter') : null;
      const width = ductShape === 'rectangular' ? readValue('ductWidth') : null;
      const height = ductShape === 'rectangular' ? readValue('ductHeight') : null;

      const areaFromDimensions = () => {
        if (ductShape === 'round') {
          if (diameter == null) return null;
          const radius = diameter / 1000 / 2;
          return Math.PI * radius * radius;
        }
        if (width == null || height == null) return null;
        return (width / 1000) * (height / 1000);
      };

      switch (nextTarget) {
        case 'volumeFlow': {
          const area = areaFromDimensions();
          if (speed != null && area != null) {
            calculatedValue = speed * area * 3600;
            resultText = `Volumenstrom: ${formatNumber(calculatedValue, 2)} m³/h`;
          } else {
            resultText = 'Geschwindigkeit und Kanalabmessungen benötigt.';
          }
          break;
        }
        case 'airSpeed': {
          const area = areaFromDimensions();
          if (volFlow != null && area != null && area !== 0) {
            calculatedValue = (volFlow / 3600) / area;
            resultText = `Luftgeschwindigkeit: ${formatNumber(calculatedValue, 2)} m/s`;
          } else {
            resultText = 'Volumenstrom und Kanalabmessungen benötigt.';
          }
          break;
        }
        case 'ductDiameter': {
          if (ductShape !== 'round') {
            resultText = 'Für rechteckige Kanäle bitte Breite oder Höhe wählen.';
            break;
          }
          if (volFlow != null && speed != null && speed !== 0) {
            if (volFlow <= 0) {
              recordError('volumeFlow', 'Darf nicht 0 sein');
            }
            const areaNeeded = (volFlow / 3600) / speed;
            if (areaNeeded > 0) {
              calculatedValue = Math.sqrt(areaNeeded / Math.PI) * 2 * 1000;
              resultText = `Durchmesser: ${formatNumber(calculatedValue, 2)} mm`;
            } else {
              resultText = 'Ungültige Werte für die Flächenberechnung.';
            }
          } else {
            resultText = 'Volumenstrom und Luftgeschwindigkeit benötigt.';
          }
          break;
        }
        case 'ductWidth': {
          if (ductShape !== 'rectangular') {
            resultText = 'Breite nur für rechteckige Kanäle verfügbar.';
            break;
          }
          if (volFlow != null && speed != null && speed !== 0) {
            if (volFlow <= 0) {
              recordError('volumeFlow', 'Darf nicht 0 sein');
            }
            const areaNeeded = (volFlow / 3600) / speed;
            const other = height;
            if (other != null && other !== 0) {
              calculatedValue = (areaNeeded / (other / 1000)) * 1000;
              resultText = `Breite: ${formatNumber(calculatedValue, 2)} mm`;
            } else {
              resultText = 'Höhe wird für die Berechnung benötigt.';
            }
          } else {
            resultText = 'Volumenstrom und Luftgeschwindigkeit benötigt.';
          }
          break;
        }
        case 'ductHeight': {
          if (ductShape !== 'rectangular') {
            resultText = 'Höhe nur für rechteckige Kanäle verfügbar.';
            break;
          }
          if (volFlow != null && speed != null && speed !== 0) {
            if (volFlow <= 0) {
              recordError('volumeFlow', 'Darf nicht 0 sein');
            }
            const areaNeeded = (volFlow / 3600) / speed;
            const other = width;
            if (other != null && other !== 0) {
              calculatedValue = (areaNeeded / (other / 1000)) * 1000;
              resultText = `Höhe: ${formatNumber(calculatedValue, 2)} mm`;
            } else {
              resultText = 'Breite wird für die Berechnung benötigt.';
            }
          } else {
            resultText = 'Volumenstrom und Luftgeschwindigkeit benötigt.';
          }
          break;
        }
        default:
          resultText = 'Ungültiges Ziel für diese Methode.';
      }
    } else if (method === 'orifice') {
      const diffPress = readValue('diffPressure', { allowZero: true, allowNegative: true });
      const alpha = readValue('alpha');
      const area = readValue('area');
      const volFlow = readValue('volumeFlow', { allowZero: true });
      const airDensity = readValue('airDensity');

      const termInsideSqrt = diffPress != null && airDensity != null ? (2 * Math.abs(diffPress)) / airDensity : null;

      switch (nextTarget) {
        case 'volumeFlow':
          if (alpha != null && area != null && termInsideSqrt != null) {
            calculatedValue = alpha * area * Math.sqrt(termInsideSqrt) * 3600;
            resultText = `Volumenstrom: ${formatNumber(calculatedValue, 2)} m³/h`;
          } else {
            resultText = 'α, A₀, Δp und ρ werden benötigt.';
          }
          break;
        case 'diffPressure':
          if (volFlow != null && alpha != null && area != null && airDensity != null && alpha !== 0 && area !== 0) {
            const term = (volFlow / 3600) / (alpha * area);
            calculatedValue = Math.pow(term, 2) * (airDensity / 2);
            resultText = `Differenzdruck: ${formatNumber(calculatedValue, 2)} Pa`;
          } else {
            resultText = 'Q, α, A₀ und ρ werden benötigt.';
          }
          break;
        case 'alpha':
          if (volFlow != null && area != null && termInsideSqrt != null && termInsideSqrt > 0) {
            calculatedValue = (volFlow / 3600) / (area * Math.sqrt(termInsideSqrt));
            resultText = `Durchflusskoeffizient Î±: ${formatForField('alpha', calculatedValue)}`;
          } else {
            resultText = 'Q, A₀, Δp und ρ werden benötigt.';
            if (termInsideSqrt != null && termInsideSqrt <= 0) {
              recordError('diffPressure', 'Darf nicht 0 sein');
            }
          }
          break;
        case 'area':
          if (volFlow != null && alpha != null && termInsideSqrt != null && termInsideSqrt > 0 && alpha !== 0) {
            calculatedValue = (volFlow / 3600) / (alpha * Math.sqrt(termInsideSqrt));
            resultText = `Öffnungsfläche A₀: ${formatForField('area', calculatedValue)} m²`;
          } else {
            resultText = 'Q, α, Δp und ρ werden benötigt.';
            if (termInsideSqrt != null && termInsideSqrt <= 0) {
              recordError('diffPressure', 'Darf nicht 0 sein');
            }
          }
          break;
        default:
          resultText = 'Ungültiges Ziel für diese Methode.';
      }
    }

    setErrors(newErrors);

    if (calculatedValue != null && Number.isFinite(calculatedValue)) {
      setValues(prev => ({ ...prev, [nextTarget]: formatForField(nextTarget, calculatedValue as number) }));
      setResult(resultText);
    } else if (!resultText) {
      setResult('Bitte Eingaben prüfen.');
    } else {
      setResult(resultText);
    }
  };

  const actionButtons = useMemo(() => (
    <>
      <Tooltip title="Formelbeschreibung anzeigen">
        <IconButton onClick={() => setInfoOpen(true)} sx={{ color: darkMode ? tokens.colors.dark.text : tokens.colors.light.text }}>
          <InfoOutlinedIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Felder zurücksetzen">
        <IconButton onClick={clearAllFields} sx={{ color: darkMode ? tokens.colors.dark.text : tokens.colors.light.text }}>
          <RestartAltIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Ergebnis kopieren">
        <IconButton onClick={handleCopyResult} sx={{ color: darkMode ? tokens.colors.dark.text : tokens.colors.light.text }}>
          <ContentCopyIcon />
        </IconButton>
      </Tooltip>
    </>
  ), [darkMode, clearAllFields, handleCopyResult]);

  if (!canViewVolumenstrom) {
    return (
      <UnitPageShell title="M99 · Volumenstromberechnung" showTTS={false}>
        <Alert severity="error">Keine Berechtigung fuer die Volumenstromberechnung.</Alert>
      </UnitPageShell>
    );
  }

  return (
    <UnitPageShell title="M99 · Volumenstromberechnung" showTTS={false} actions={actionButtons}>
      <Card elevation={2}>
        <CardContent>
          <Stack spacing={3}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: { xs: 'flex-start', md: 'center' } }}>
              <FormControl fullWidth sx={{ maxWidth: 380 }}>
                <InputLabel id="method-select-label">Berechnungsmethode</InputLabel>
                <Select
                  labelId="method-select-label"
                  value={method}
                  label="Berechnungsmethode"
                  onChange={handleMethodChange}
                >
                  {METHOD_OPTIONS.map(option => (
                    <MenuItem key={option.key} value={option.key}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Wähle die gewünschte Messmethode aus.</FormHelperText>
              </FormControl>
              <Box sx={{ flexGrow: 1 }} />
              <Button 
                variant="contained" 
                sx={{ 
                  backgroundColor: tokens.colors.primary, 
                  '&:hover': { backgroundColor: tokens.colors.primaryHover } 
                }} 
                onClick={handleCalculate}
              >
                Berechnen
              </Button>
            </Box>

            <Divider />

            {method === 'anemometer' && (
              <Stack spacing={1}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Kanalform
                </Typography>
                <RadioGroup row value={ductShape} onChange={handleDuctShapeChange}>
                  <FormControlLabel value="round" control={<Radio />} label="Rund" />
                  <FormControlLabel value="rectangular" control={<Radio />} label="Rechteckig" />
                </RadioGroup>
              </Stack>
            )}

            <Typography variant="subtitle2" sx={{ color: darkMode ? tokens.colors.dark.textSecondary : tokens.colors.light.textSecondary }}>
              Tipp: Lasse genau ein Eingabefeld leer. Dieses Feld wird als Zielgröße berechnet.
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              {method === 'ventilator' && (
                <>
                  {renderField('volumeFlow')}
                  {renderField('effectivePressure')}
                  {renderField('kFactor')}
                </>
              )}

              {method === 'anemometer' && (
                <>
                  {renderField('volumeFlow')}
                  {renderField('airSpeed')}
                  {ductShape === 'round' ? renderField('ductDiameter') : renderField('ductWidth')}
                  {ductShape === 'rectangular' && renderField('ductHeight')}
                </>
              )}

              {method === 'orifice' && (
                <>
                  {renderField('volumeFlow')}
                  {renderField('diffPressure')}
                  {renderField('alpha')}
                  {renderField('area')}
                  {renderField('airDensity')}
                </>
              )}
            </Box>

            <Divider />

            <Box sx={{ 
              p: 2, 
              borderRadius: 2, 
              backgroundColor: darkMode ? 'rgba(178, 20, 15, 0.1)' : 'rgba(178, 20, 15, 0.05)',
              border: `1px solid ${tokens.colors.primary}33`
            }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: tokens.colors.primary, 
                  fontWeight: 700, 
                  textAlign: 'center' 
                }}
              >
                {result}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Information zur Methode</DialogTitle>
        <DialogContent>
          <Typography sx={{ whiteSpace: 'pre-line' }}>{METHOD_DESCRIPTIONS[method]}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>{snackbar.message}</Alert> : undefined}
      </Snackbar>
    </UnitPageShell>
  );
};

export default Page;

